import React, {useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Edit} from "@mui/icons-material";
import EditableCollectionsComponent from "../../../../../components/fields/EditableCollectionsComponent";
import {t} from "../../../../../store/localization/TranslMessasge";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import {ElementDetailTranslationKey} from "../ElementDetailTranslationKey";
import {ILabelDto} from "../../../../../common/apis/label/ILabelDto";
import {EditableLabelsComponent} from "../../../../../components/fields/label/EditableLabelsComponent";
import {IApplicationState} from "../../../../../store/Store";
import constructElementDetailController, {ElementDetailController} from "../controller/ElementDetailController";
import elementService from "../service/ElementService";
import diagramService from "../../diagrams/service/DiagramService";
import Grid from "../../../../../components/dialogs/Grid";
import TextField from "../../../../../components/fields/textfield/TextField";
import {ArchimateElement} from "../../../../../common/archimate/ArchimateElement";
import {ElementTranslationKey} from "../../elements/ElementTranslationKey";
import {DatePicker} from "../../../../../components/fields/time/DatePicker";
import SectionHeader from "../../../../../components/section/SectionHeader";
import SectionActionBar from "../../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../../components/section/SectionActionButton";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import ElementBasicInfoUpdateDialog from "./ElementBasicInfoUpdateDialog";

interface ElementBasicInfoPanelProps {
    element: ElementDto;
    onPropertyUpdate: () => void;
    onElementUpdated?: (id: string) => void;
}

export default function ElementBasicInfoPanel({element, onPropertyUpdate, onElementUpdated}: Readonly<ElementBasicInfoPanelProps>) {

    const elementDetailController = useRef<ElementDetailController>(constructElementDetailController(elementService, diagramService));
    const labels = useSelector((state: IApplicationState) => state.pages.common.options.labels.resource);
    const states = useSelector((state: IApplicationState) => state.pages.common.options.states.resource || []);

    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const stateName = element.state
            ? (states.find(s => s.code === element.state)?.name ?? element.state)
            : "";

    return (
            <>
                <Grid container>
                    <Grid item xs={12}>
                        <SectionHeader title={t(ElementDetailTranslationKey.DETAILS)}>
                            <SectionActionBar>
                                <SectionActionButton
                                        icon={<Edit fontSize="inherit"/>}
                                        tooltip={t(CommonTranslationKey.EDIT)}
                                        onClick={() => setEditDialogOpen(true)}
                                        visible={element.acl.canUpdate}/>
                            </SectionActionBar>
                        </SectionHeader>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                id="item-detail-basic-panel-name"
                                label={t(ElementTranslationKey.NAME)}
                                value={element.name || ""}
                                readOnly={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                id="item-detail-basic-panel-id"
                                label={t(ElementDetailTranslationKey.IDENTIFIER)}
                                value={element.identifier || ""}
                                readOnly={true}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                                id="item-detail-basic-panel-type"
                                label={t(ElementDetailTranslationKey.TYPE)}
                                value={ArchimateElement.findByStandardName(element.type)?.visibleName || ""}
                                readOnly={true}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                                id="item-detail-basic-panel-stereotype"
                                label={t(ElementDetailTranslationKey.STEREOTYPE)}
                                value={element.stereotype?.name || ""}
                                readOnly={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                id="item-detail-basic-panel-desc"
                                label={t(ElementDetailTranslationKey.DESCRIPTION)}
                                value={element.description || ""}
                                readOnly={true}
                                multiline={true}
                                rows={8}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                                id="item-detail-basic-panel-state"
                                label={t(ElementDetailTranslationKey.STATE)}
                                value={stateName}
                                readOnly={true}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <DatePicker
                                label={t(ElementDetailTranslationKey.VALID_FROM)}
                                value={element.validFrom ? new Date(element.validFrom) : null}
                                onChange={() => {
                                }}
                                readOnly={true}/>
                    </Grid>
                    <Grid item  xs={12} sm={4}>
                        <DatePicker
                                label={t(ElementDetailTranslationKey.VALID_THRU)}
                                value={element.validThru ? new Date(element.validThru) : null}
                                onChange={() => {
                                }}
                                readOnly={true}/>
                    </Grid>
                </Grid>

                <Grid container>
                    <Grid item xs={12}>
                        <SectionHeader title={t(ElementDetailTranslationKey.LABELS_AND_COLLECTIONS)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <EditableLabelsComponent textFieldId={"item-detail-basic-panel-labels"}
                                publicLabelsAssigningPermitted={element.acl.canUpdatePublicLabels}
                                allLabels={labels}
                                initiallyAssignedLabels={element.labels}
                                doUpdate={(labels: ILabelDto[]) => elementDetailController.current.updateLabels(element.identifier, labels)}
                                onSuccessfulUpdate={() => onPropertyUpdate()}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EditableCollectionsComponent
                                textFieldId={"item-detail-basic-panel-collections"}
                                assignedCollectionCodes={element.collections.map(collection => collection.code)}
                                doUpdate={(items) => elementDetailController.current.updateCollections(element.identifier, items)}
                                onSuccessfulUpdate={() => onPropertyUpdate()}
                                readonly={!element.acl.canUpdateCollections}
                        />
                    </Grid>
                </Grid>

                {editDialogOpen &&
                        <ElementBasicInfoUpdateDialog
                                open={true}
                                element={element}
                                onClose={() => setEditDialogOpen(false)}
                                onUpdate={() => {
                                    onPropertyUpdate();
                                    onElementUpdated?.(element.identifier);
                                }}/>
                }
            </>
    );
}
